import { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar, Mail, Phone, Linkedin, CheckCircle, Award, Users, TrendingUp, Clock, Target, Share2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import './MCM.css';

const clients = [
  'EDENRED', 'MAROC TELECOM', 'SFR', 'SOCIÉTÉ GÉNÉRALE', 'RATP DEV',
  'PCM COMMUNICATION', 'PCM CONSULTING', 'PCM YOUR PACKAGING',
  'COMANAV', 'NXO', 'FAYAT', 'INNEXIA', 'KEYLEO CONSULTING',
  'RATPCAP IFD', 'STS SALÈVE', 'ATELIERS DE CHAMPIONNET'
];

const services = [
  {
    icon: Target,
    title: 'Direction de Projets IT & SI',
    description: 'Pilotage de projets IT complexes, coordination équipes, suivi budgets et KPI. Expertise en gestion d\'applications métiers et transformation digitale.',
    points: [
      'Gestion de projets IT transverses',
      'Coordination équipes multiples',
      'Pilotage budgets & KPI',
      'Conduite du changement'
    ]
  },
  {
    icon: TrendingUp,
    title: 'Conseil en Infrastructure & Réseaux',
    description: 'Expertise en architecture SI, infrastructure réseau WAN/WiFi, intégration systèmes et optimisation des performances opérationnelles.',
    points: [
      'Architecture SI & réseaux',
      'Infrastructure WAN/WiFi',
      'Optimisation performances',
      'Intégration & migrations'
    ]
  }
];

const testimonials = [
  {
    name: 'Direction RATP Dev',
    role: 'Directeur SI',
    company: 'RATP Dev',
    text: 'Mohammed a démontré une expertise exceptionnelle dans le pilotage du projet Windows 11. Sa capacité à coordonner les équipes et à maintenir la qualité tout au long du déploiement a été remarquable.'
  },
  {
    name: 'Responsable Infrastructure',
    role: 'Manager Technique',
    company: 'Société Générale',
    text: 'En tant que Delivery Manager L2 WAN/WiFi, Mohammed a apporté une vision opérationnelle claire et des résultats mesurables. Un vrai professionnel du pilotage d\'équipe.'
  },
  {
    name: 'Chef de Projet',
    role: 'Project Lead',
    company: 'SFR Groupe Altice',
    text: 'Excellence dans la coordination de projets complexes. Mohammed allie parfaitement expertise technique et compétences managériales pour livrer des projets dans les délais.'
  }
];

const blogPosts = [
  {
    title: 'L\'importance de la gouvernance SI dans les projets IT',
    date: '15 Mars 2026',
    category: 'Stratégie',
    excerpt: 'La gouvernance SI est un pilier essentiel pour garantir l\'alignement des projets IT avec les objectifs métiers...'
  },
  {
    title: 'Migration infrastructure : les clés du succès',
    date: '8 Mars 2026',
    category: 'Infrastructure',
    excerpt: 'Les projets de migration infrastructure nécessitent une planification rigoureuse et une coordination exemplaire...'
  },
  {
    title: 'Conduite du changement : accompagner les utilisateurs',
    date: '1 Mars 2026',
    category: 'Management',
    excerpt: 'La réussite d\'un projet IT passe par l\'adhésion des utilisateurs. Voici les bonnes pratiques...'
  }
];

function MCMWebsite() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ⚠️ FORMSPREE : remplacer YOUR_FORM_ID par l'ID fourni par Formspree
  // Étapes : (1) créer un compte sur https://formspree.io avec mohammed.toummini@mentisconsilium.com
  //         (2) créer un nouveau formulaire ; (3) copier l'ID (ex: "xayznvqr")
  //         (4) remplacer ci-dessous puis rebuild + redeploy
  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xqewokze';

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);

    // Si Formspree pas encore configuré → fallback mailto
    if (FORMSPREE_ENDPOINT.includes('YOUR_FORM_ID')) {
      const mailto = `mailto:mohammed.toummini@mentisconsilium.com?subject=${encodeURIComponent(
        'Contact MCM - ' + (formData.get('company') || formData.get('name'))
      )}&body=${encodeURIComponent(
        `Nom: ${formData.get('name')}\nEmail: ${formData.get('email')}\nEntreprise: ${formData.get('company')}\n\nMessage:\n${formData.get('message')}`
      )}`;
      window.location.href = mailto;
      toast.success('Ouverture de votre client mail…', {
        description: 'Vérifiez votre messagerie pour finaliser l\'envoi.'
      });
      return;
    }

    try {
      const payload = {
        name: formData.get('name'),
        email: formData.get('email'),
        company: formData.get('company') || '',
        message: formData.get('message'),
        _subject: 'Contact MCM - ' + (formData.get('company') || formData.get('name'))
      };
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success('Message envoyé !', { description: 'Nous vous recontacterons rapidement.' });
        form.reset();
      } else {
        const data = await res.json().catch(() => ({}));
        const msg = data?.errors?.[0]?.message || 'Réessayez ou écrivez à mohammed.toummini@mentisconsilium.com';
        toast.error('Erreur d\'envoi', { description: msg });
      }
    } catch {
      toast.error('Erreur réseau', { description: 'Vérifiez votre connexion.' });
    }
  };

  return (
    <div className="mcm-website">
      {/* Header */}
      <motion.header 
        className={`mcm-header ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="mcm-container">
          <div className="header-content">
            <div className="logo-section">
              <img 
                src="https://customer-assets.emergentagent.com/job_muslim-diet-planner/artifacts/7bi7z058_d9b862df-693c-4dd5-b06e-4bc926d6dd37.jpeg" 
                alt="MCM Logo" 
                className="mcm-logo"
              />
            </div>
            <nav className="main-nav" data-testid="main-nav">
              <a href="#accueil">Accueil</a>
              <a href="#apropos">À propos</a>
              <a href="#services">Services</a>
              <a href="#references">Références</a>
              <Link to="/audit" data-testid="nav-audit-link">Audit gratuit</Link>
              <a href="#blog">Actualités</a>
              <a href="#contact">Contact</a>
            </nav>
            <Button 
              className="cta-button"
              onClick={() => document.getElementById('calendly').scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-header-button"
            >
              <Calendar className="icon-sm" />
              Prendre RDV
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section id="accueil" className="hero-section">
        <div className="hero-background">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/c04fa1dc-036c-4043-b997-0237d4270cba/images/e3fef8c8e9daab59011044cbbd83ce41eeb48af2b590968b82c2954d34b38067.png" 
            alt="Background" 
            className="hero-bg-image"
          />
          <div className="hero-overlay" />
        </div>
        <div className="mcm-container hero-container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="hero-label"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              MENTIS CONSILIUM
            </motion.div>
            <h1 className="hero-title" data-testid="hero-title">
              Strategic & Digital<br />Consulting Excellence
            </h1>
            <p className="hero-description">
              Plus de 20 ans d'expertise en direction de projets IT, infrastructure et transformation digitale. 
              MCM accompagne les grandes entreprises dans leurs projets stratégiques les plus critiques.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">20+</div>
                <div className="stat-label">Années d'expérience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">15+</div>
                <div className="stat-label">Grands comptes</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100+</div>
                <div className="stat-label">Projets livrés</div>
              </div>
            </div>
            <div className="hero-buttons">
              <Link to="/audit" className="audit-cta-hero" data-testid="hero-audit-button">
                <Sparkles size={18} /> Audit IT gratuit (2 min)
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="secondary-button"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-contact-button"
              >
                Nous contacter
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Clients Marquee */}
      <section className="clients-marquee-section">
        <div className="marquee-label">ILS NOUS FONT CONFIANCE</div>
        <Marquee gradient={false} speed={40} className="clients-marquee">
          {clients.map((client, idx) => (
            <div key={idx} className="client-item">
              <span>{client}</span>
              <span className="separator">•</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* About President */}
      <section id="apropos" className="about-section">
        <div className="mcm-container">
          <div className="about-grid">
            <motion.div 
              className="about-image"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_muslim-diet-planner/artifacts/y3cwhzgr_IMG_6138.jpeg" 
                alt="Mohammed Toummini" 
                className="president-photo"
              />
            </motion.div>
            <motion.div 
              className="about-content"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="section-label">À PROPOS</div>
              <h2 className="section-title">Mohammed Toummini</h2>
              <div className="president-role">Président & Fondateur de MCM</div>
              <div className="certifications">
                <div className="cert-badge">
                  <Award className="cert-icon" />
                  <span>PMP® Certified</span>
                </div>
                <div className="cert-badge">
                  <Award className="cert-icon" />
                  <span>Ingénieur Télécoms & Réseaux</span>
                </div>
              </div>
              <div className="about-text">
                <p>
                  Fort de plus de 20 ans d'expérience en tant que Chef de Projet SI et Delivery Manager, 
                  j'ai piloté des projets stratégiques pour des grands comptes français et internationaux. 
                  Ma certification PMP® et mon parcours chez Société Générale, RATP Dev, SFR et Maroc Telecom 
                  m'ont permis de développer une expertise unique en direction de projets IT et infrastructure.
                </p>
                <blockquote className="quote">
                  "L'excellence opérationnelle naît de la combinaison entre vision stratégique, 
                  rigueur méthodologique et accompagnement humain."
                </blockquote>
                <p>
                  Actuellement Delivery Manager Operational de l'équipe L2 WAN/WiFi chez Société Générale, 
                  je mets mon expertise au service des entreprises qui souhaitent réussir leurs transformations IT 
                  et optimiser leurs infrastructures critiques.
                </p>
              </div>
              <Button 
                className="linkedin-button"
                onClick={() => window.open('https://www.linkedin.com/in/mohammed-toummini', '_blank')}
                data-testid="linkedin-button"
              >
                <Linkedin className="icon-sm" />
                Voir mon profil LinkedIn
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="services-section">
        <div className="mcm-container">
          <div className="section-header-center">
            <div className="section-label">NOS SERVICES</div>
            <h2 className="section-title">Domaines d'expertise</h2>
            <p className="section-description">
              MCM vous accompagne sur l'ensemble de vos projets stratégiques IT et infrastructure
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
              >
                <Card className="service-card" data-testid={`service-card-${idx}`}>
                  <CardContent className="service-content">
                    <div className="service-icon">
                      <service.icon size={32} />
                    </div>
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-description">{service.description}</p>
                    <ul className="service-points">
                      {service.points.map((point, pidx) => (
                        <li key={pidx}>
                          <CheckCircle className="check-icon" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* References */}
      <section id="references" className="references-section">
        <div className="mcm-container">
          <div className="section-header-center">
            <div className="section-label">RÉFÉRENCES</div>
            <h2 className="section-title">Ils nous ont fait confiance</h2>
            <p className="section-description">
              Des collaborations avec les leaders de leurs secteurs
            </p>
          </div>
          <div className="references-grid">
            {clients.slice(0, 12).map((client, idx) => (
              <motion.div
                key={idx}
                className="reference-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                data-testid={`reference-${idx}`}
              >
                <div className="reference-name">{client}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="mcm-container">
          <div className="section-header-center">
            <div className="section-label">TÉMOIGNAGES</div>
            <h2 className="section-title">Ce qu'ils disent de nous</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
              >
                <Card className="testimonial-card" data-testid={`testimonial-${idx}`}>
                  <CardContent className="testimonial-content">
                    <div className="quote-mark">"</div>
                    <p className="testimonial-text">{testimonial.text}</p>
                    <div className="testimonial-author">
                      <div className="author-name">{testimonial.name}</div>
                      <div className="author-role">{testimonial.role}</div>
                      <div className="author-company">{testimonial.company}</div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog */}
      <section id="blog" className="blog-section">
        <div className="mcm-container">
          <div className="section-header-center">
            <div className="section-label">ACTUALITÉS</div>
            <h2 className="section-title">Derniers articles</h2>
          </div>
          <div className="blog-grid">
            {blogPosts.map((post, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <Card className="blog-card" data-testid={`blog-post-${idx}`}>
                  <CardContent className="blog-content">
                    <div className="blog-meta">
                      <span className="blog-category">{post.category}</span>
                      <span className="blog-date">
                        <Clock size={14} />
                        {post.date}
                      </span>
                    </div>
                    <h3 className="blog-title">{post.title}</h3>
                    <p className="blog-excerpt">{post.excerpt}</p>
                    <div className="blog-actions">
                      <Button variant="link" className="blog-link">
                        Lire la suite →
                      </Button>
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://mentisconsilium.com/#blog')}&title=${encodeURIComponent(post.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="blog-share-linkedin"
                        title="Partager sur LinkedIn"
                        data-testid={`share-linkedin-${idx}`}
                      >
                        <Share2 size={14} /> LinkedIn
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendly */}
      <section id="calendly" className="calendly-section">
        <div className="mcm-container">
          <div className="section-header-center">
            <div className="section-label">RENDEZ-VOUS</div>
            <h2 className="section-title">Prenez rendez-vous</h2>
            <p className="section-description">
              Consultation initiale ou audit de vos projets IT - Choisissez le créneau qui vous convient
            </p>
          </div>
          <Card className="calendly-card">
            <CardContent className="calendly-content">
              {/* CALENDLY : URL configurée pour le compte mohammed.toummini@mentisconsilium.com */}
              <iframe
                src="https://calendly.com/mohammed-toummini-mentisconsilium/30min"
                width="100%"
                height="700"
                frameBorder="0"
                title="Calendrier de prise de rendez-vous Calendly"
                data-testid="calendly-iframe"
              />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="contact-section">
        <div className="mcm-container">
          <div className="contact-grid">
            <motion.div 
              className="contact-info"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="section-label">CONTACT</div>
              <h2 className="section-title">Parlons de votre projet</h2>
              <p className="contact-description">
                Vous avez un projet IT ou infrastructure à piloter ? Contactez-nous pour échanger sur vos besoins.
              </p>
              <div className="contact-details">
                <div className="contact-item">
                  <Phone className="contact-icon" />
                  <div>
                    <div className="contact-label">Téléphone</div>
                    <a href="tel:+33783282634" className="contact-value">07 83 28 26 34</a>
                  </div>
                </div>
                <div className="contact-item">
                  <Linkedin className="contact-icon" />
                  <div>
                    <div className="contact-label">LinkedIn</div>
                    <a 
                      href="https://www.linkedin.com/in/mohammed-toummini" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="contact-value"
                    >
                      Mohammed Toummini
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div 
              className="contact-form-wrapper"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="contact-form-card">
                <CardContent className="contact-form-content">
                  <form onSubmit={handleContactSubmit} data-testid="contact-form">
                    <div className="form-group">
                      <Label htmlFor="name">Nom complet</Label>
                      <Input 
                        id="name" 
                        type="text" 
                        placeholder="Jean Dupont" 
                        required 
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="email">Email professionnel</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="jean.dupont@entreprise.fr" 
                        required 
                        data-testid="contact-email"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="company">Entreprise</Label>
                      <Input 
                        id="company" 
                        type="text" 
                        placeholder="Nom de votre entreprise" 
                        data-testid="contact-company"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="message">Message</Label>
                      <Textarea 
                        id="message" 
                        rows={5} 
                        placeholder="Décrivez votre projet ou besoin..." 
                        required 
                        data-testid="contact-message"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="submit-button" 
                      size="lg"
                      data-testid="contact-submit"
                    >
                      <Mail className="icon-sm" />
                      Envoyer le message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mcm-footer">
        <div className="mcm-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img 
                src="https://customer-assets.emergentagent.com/job_muslim-diet-planner/artifacts/7bi7z058_d9b862df-693c-4dd5-b06e-4bc926d6dd37.jpeg" 
                alt="MCM Logo" 
                className="footer-logo"
              />
              <p className="footer-tagline">
                Strategic & Digital Consulting Excellence
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Navigation</h4>
                <a href="#accueil">Accueil</a>
                <a href="#apropos">À propos</a>
                <a href="#services">Services</a>
                <a href="#references">Références</a>
              </div>
              <div className="footer-column">
                <h4>Ressources</h4>
                <a href="#blog">Actualités</a>
                <a href="#contact">Contact</a>
                <a href="#calendly">Prendre RDV</a>
              </div>
              <div className="footer-column">
                <h4>Contact</h4>
                <a href="tel:+33783282634">07 83 28 26 34</a>
                <a href="https://www.linkedin.com/in/mohammed-toummini" target="_blank" rel="noopener noreferrer">
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 MCM - Mentis Consilium. Tous droits réservés.</p>
            <p className="footer-legal">
              SASU au capital variable • SIRET : 102 709 086 00015
            </p>
            <p className="footer-legal" style={{ marginTop: '0.75rem' }}>
              <a href="/mentions-legales" style={{ color: 'inherit', textDecoration: 'underline' }}>Mentions légales</a>
              {' · '}
              <a href="/politique-confidentialite" style={{ color: 'inherit', textDecoration: 'underline' }}>Politique de confidentialité</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default MCMWebsite;